import { useMutation, useQuery } from "@apollo/client";
import {
  GET_MARKETPLACE_LISTINGS,
  GET_MY_MARKETPLACE_LISTINGS,
} from "../../../../platform/graphql/queries/marketplace";
import {
  CREATE_MARKETPLACE_LISTING,
  UPDATE_MARKETPLACE_LISTING,
  UPDATE_MARKETPLACE_LISTING_STATUS,
} from "../../../../platform/graphql/mutations/marketplace";
import {
  MarketplaceListing,
  MarketplaceListingStatus,
  MarketplaceListingType,
} from "../marketplaceStorage";

function computeListingWindow() {
  const isExpired = false;
  return { isExpired };
}

type ListingFilters = {
  q?: string;
  type?: MarketplaceListingType | "ALL";
  maxPrice?: number | null;
  status?: MarketplaceListingStatus;
};

function normalizeListing(input: any): MarketplaceListing {
  const imageUrlsRaw = Array.isArray(input?.imageUrls)
    ? input.imageUrls
    : input?.imageUrl
      ? [input.imageUrl]
      : [];
  const imageUrls = imageUrlsRaw
    .map((u: any) => String(u || "").trim())
    .filter(Boolean);
  const { isExpired } = computeListingWindow();
  return {
    id: String(input?.id || ""),
    type: input?.type === "AFH_SALE" ? "AFH_SALE" : "ELDER_GIFT",
    status:
      input?.status === "SOLD"
        ? "SOLD"
        : input?.status === "PAUSED"
          ? "PAUSED"
          : "ACTIVE",
    title: String(input?.title || ""),
    description: String(input?.description || ""),
    price: Number(input?.price || 0),
    city: String(input?.city || ""),
    state: String(input?.state || ""),
    contactEmail: String(input?.contactEmail || ""),
    contactPhone: input?.contactPhone ? String(input.contactPhone) : undefined,
    ownerUserId: input?.ownerUserId ? String(input.ownerUserId) : undefined,
    ownerBusinessId: input?.ownerBusinessId ? String(input.ownerBusinessId) : undefined,
    ownerBusinessName: input?.ownerBusinessName
      ? String(input.ownerBusinessName)
      : undefined,
    beds: input?.beds == null ? undefined : Number(input.beds),
    capacity: input?.capacity == null ? undefined : Number(input.capacity),
    occupancy: input?.occupancy == null ? undefined : Number(input.occupancy),
    giftCategory: input?.giftCategory ? String(input.giftCategory) : undefined,
    ageGroup: input?.ageGroup ? String(input.ageGroup) : undefined,
    giftImageUrl: input?.giftImageUrl ? String(input.giftImageUrl) : undefined,
    imageUrl: input?.imageUrl ? String(input.imageUrl) : undefined,
    imageUrls,
    createdAt: String(input?.createdAt || new Date().toISOString()),
    updatedAt: input?.updatedAt ? String(input.updatedAt) : undefined,
    expiresAt: undefined,
    isExpired,
  };
}

export function useMarketplaceListings(filters: ListingFilters) {
  const q = String(filters?.q || "").trim();
  const maxPrice = Number(filters?.maxPrice || 0);
  const type = filters?.type && filters.type !== "ALL" ? filters.type : undefined;

  const query = useQuery(GET_MARKETPLACE_LISTINGS, {
    variables: {
      q: q || undefined,
      type,
      maxPrice: maxPrice > 0 ? maxPrice : undefined,
      status: filters?.status,
      limit: 120,
      offset: 0,
    },
    fetchPolicy: "cache-and-network",
  });

  const fromServer = (query.data?.marketplaceListings?.items || []).map(normalizeListing);
  return {
    listings: fromServer,
    loading: query.loading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCreateMarketplaceListing() {
  const [mutate, state] = useMutation(CREATE_MARKETPLACE_LISTING);

  const create = async (input: any) => {
    const serverInput = {
      type: input?.type,
      title: input?.title,
      description: input?.description,
      price: input?.price,
      city: input?.city,
      state: input?.state,
      contactEmail: input?.contactEmail,
      contactPhone: input?.contactPhone,
      ownerBusinessId: input?.ownerBusinessId,
      ownerBusinessName: input?.ownerBusinessName,
      beds: input?.beds,
      capacity: input?.capacity,
      occupancy: input?.occupancy,
      giftCategory: input?.giftCategory,
      ageGroup: input?.ageGroup,
      giftImageUrl: input?.giftImageUrl,
      imageUrls: input?.imageUrls,
    };
    try {
      const res = await mutate({ variables: { input: serverInput } });
      const listing = res?.data?.createMarketplaceListing?.listing;
      if (!listing) throw new Error("No listing returned.");
      return { listing: normalizeListing(listing) };
    } catch (err: any) {
      const msg =
        err?.graphQLErrors?.[0]?.message ||
        err?.message ||
        "Failed to create listing.";
      throw new Error(msg);
    }
  };

  return { create, ...state };
}

export function useMyMarketplaceListings(ownerUserId: string) {
  const query = useQuery(GET_MY_MARKETPLACE_LISTINGS, {
    variables: { limit: 120, offset: 0 },
    skip: !ownerUserId,
    fetchPolicy: "cache-and-network",
  });

  const fromServer = (query.data?.myMarketplaceListings?.items || []).map(normalizeListing);

  return {
    listings: fromServer,
    loading: query.loading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useUpdateMarketplaceListingStatus() {
  const [mutate, state] = useMutation(UPDATE_MARKETPLACE_LISTING_STATUS);

  const updateStatus = async (id: string, status: MarketplaceListingStatus) => {
    try {
      await mutate({ variables: { id, status } });
      return {};
    } catch (err: any) {
      const msg =
        err?.graphQLErrors?.[0]?.message ||
        err?.message ||
        "Failed to update listing status.";
      throw new Error(msg);
    }
  };

  return { updateStatus, ...state };
}

export function useUpdateMarketplaceListing() {
  const [mutate, state] = useMutation(UPDATE_MARKETPLACE_LISTING);

  const updateListing = async (id: string, patch: any) => {
    const serverPatch = {
      title: patch?.title,
      description: patch?.description,
      price: patch?.price,
      city: patch?.city,
      state: patch?.state,
      contactEmail: patch?.contactEmail,
      contactPhone: patch?.contactPhone,
      ownerBusinessName: patch?.ownerBusinessName,
      beds: patch?.beds,
      capacity: patch?.capacity,
      occupancy: patch?.occupancy,
      giftCategory: patch?.giftCategory,
      ageGroup: patch?.ageGroup,
      giftImageUrl: patch?.giftImageUrl,
      imageUrls: patch?.imageUrls,
    };

    try {
      const res = await mutate({ variables: { id, patch: serverPatch } });
      const listing = res?.data?.updateMarketplaceListing?.listing;
      if (!listing) throw new Error("No listing returned.");
      return { listing: normalizeListing(listing) };
    } catch (err: any) {
      const msg =
        err?.graphQLErrors?.[0]?.message ||
        err?.message ||
        "Failed to update listing.";
      throw new Error(msg);
    }
  };

  return { updateListing, ...state };
}
